import React, { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { AdminSidebar } from './AdminSidebar';
import { AdminDesktopSidebar } from './AdminDesktopSidebar';
import { AdminBottomNavigation } from './AdminBottomNavigation';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { useAuth, useCompany } from '../hooks/useAuth';
import { ServiceSuspendedBanner } from './ServiceSuspendedBanner';
import { AppColors } from '../theme/colors';

interface ResponsiveAdminLayoutProps {
  children: ReactNode;
  /**
   * 画面の見出し。渡すとレイアウト側で中ヘッダーを描く。
   * 無料実証の期間を見出しの右に並べるため、ここで一緒に扱う。
   */
  pageTitle?: string;
  currentTab?: 'voice' | 'thanks' | 'theme';
  showBottomNav?: boolean;
  maxWidth?: string | number;
}

/**
 * 管理画面用のレスポンシブレイアウトコンポーネント
 * PC版: ヘッダー + 固定サイドバー + メインコンテンツ
 * モバイル版: ヘッダー + ドロワーサイドバー + メインコンテンツ + ボトムナビ
 */
export const ResponsiveAdminLayout: React.FC<ResponsiveAdminLayoutProps> = ({
  children,
  pageTitle,
  currentTab,
  showBottomNav = true,
  maxWidth = '1000px',
}) => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { user } = useAuth();
  const { company } = useCompany(user?.uid || null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface, display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <Header 
        onMenuClick={() => setDrawerOpen(true)}
        onSettingsClick={() => navigate('/settings')}
        showAdminBadge={true}
        freeTrialEndDate={company?.isFreeTrial ? company.freeTrialEndDate : undefined}
        companyName={company?.companyName}
        corporateCode={company?.corporateCode}
      />

      {/* モバイル用サイドバー */}
      {!isDesktop && <AdminSidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} />}

      {/* レイアウトコンテナ */}
      <Box sx={{ display: 'flex', flex: 1, pt: '64px' }}>
        {/* PC版用固定サイドバー */}
        {isDesktop && <AdminDesktopSidebar />}

        {/* メインコンテンツ */}
        <Box sx={{ 
          flex: 1,
          pb: isDesktop ? 0 : (showBottomNav ? 10 : 2),
          marginLeft: isDesktop ? '240px' : 0,
          width: isDesktop ? 'calc(100% - 240px)' : '100%',
        }}>

          {/* 中ヘッダー。見出しの右に無料実証の期間を並べる */}
          {pageTitle && (
            <Box sx={{ width: '100%', p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black' }}>
                {pageTitle}
              </Typography>
            </Box>
          )}

          {/* サービス停止バナー */}
          {company?.serviceStatus === 'suspended' && (
            <Box sx={{ p: 2 }}>
              <ServiceSuspendedBanner
                companyName={company?.companyName}
                suspendedAt={company?.suspendedAt?.toDate ? company.suspendedAt.toDate() : undefined}
                overdueInvoices={company?.overdueInvoices}
                suspensionReason={company?.suspensionReason}
              />
            </Box>
          )}
          {children}
        </Box>
      </Box>

      {/* モバイル版のみボトムナビゲーション表示 */}
      {!isDesktop && showBottomNav && (
        <AdminBottomNavigation 
          currentTab={currentTab}
          onTabChange={(tab) => {
            if (tab === 'theme') {
              navigate('/admin/theme-posts');
            } else {
              navigate(`/admin?tab=${tab}`);
            }
          }}
        />
      )}
    </Box>
  );
};

